import { useMemo, useState } from 'react'

import { GetRowIdParams, RowDragEndEvent, ValueGetterParams } from 'ag-grid-enterprise'
import { RowDragModule } from 'ag-grid-enterprise'
import { CustomCellEditorProps } from 'ag-grid-react'
import { Trash2 } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { CustomAgGrid, createColDefs, createContextMenu } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { Badge } from '@fortem-monorepo/react-ui'
import { MarketSummary } from '@fortem-monorepo/web-sdk'

import { useMarketSummaryActions } from '@/pages/instruments/market-summary/market-summary-actions'
import { useMarketSummaryContext } from '@/pages/instruments/market-summary/market-summary-context'
import { useUpdateMetricsDisplayOrderMutation } from '@/pages/instruments/market-summary/market-summary.mutations'

export interface MarketSummaryMetricsTabProps {}

export const MarketSummaryMetricsTab: React.FC<MarketSummaryMetricsTabProps> = () => {
  const { metricsQuery, hasWriteAccess, view } = useMarketSummaryContext()

  const { removeMetric, actionState } = useMarketSummaryActions()

  const _data = useMemo(() => {
    return metricsQuery.data?.sort((a, b) => {
      return a.display_order - b.display_order
    })
  }, [metricsQuery.data])

  const _coldefs = useMemo(() => {
    return colDefs()
  }, [colDefs])

  const contextMenu = createContextMenu<MarketSummary.Metric>((params) => {
    const row = params.node?.data
    const defaults = params.defaultItems ?? []
    if (!row || params.node?.group) return defaults

    return [
      ...(hasWriteAccess
        ? [
            {
              name: 'Remove Metric',
              icon: renderToString(<Trash2 size={16} />),
              cssClasses: ['text-red-600!'],
              disabled: actionState !== null,
              action: () => removeMetric({ display_id: row.display_id, display_metric_id: row.display_metric_id }),
            },
            'separator',
          ]
        : ([] as any)),
      ...defaults,
    ]
  })

  const [updating, setUpdating] = useState(false)
  const updateDisplayOrderMutation = useUpdateMetricsDisplayOrderMutation()

  return (
    <div className={'flex-1 h-full'}>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery query={metricsQuery} title="Values" />
        <WidgetContent loading={metricsQuery.isLoading}>
          <CustomAgGrid
            modules={[RowDragModule]}
            getContextMenuItems={contextMenu}
            rowData={_data}
            updating={updating}
            onEditingChange={setUpdating}
            query={metricsQuery}
            getRowId={(row: GetRowIdParams<MarketSummary.Metric>) => row.data.display_metric_id.toString()}
            columnDefs={_coldefs}
            rowDragManaged={true}
            domLayout={'normal'}
            onRowDragEnd={async (params: RowDragEndEvent<MarketSummary.Metric>) => {
              setUpdating(true)
              const display_metric_ids = params.api.getRenderedNodes().map((node) => node.data!.display_metric_id)
              await updateDisplayOrderMutation.mutateAsync({ display_id: view?.display_id!, display_metric_ids })
              setUpdating(false)
            }}
          />
        </WidgetContent>
      </Widget>
    </div>
  )
}

const colDefs = () => {
  return createColDefs<MarketSummary.Metric>([
    {
      field: 'display_order',
      headerName: 'Display Order',
      sortable: false,
      filter: false,
      rowDrag: true,
    },
    {
      field: 'definition',
      headerName: 'Definition',
      sortable: false,
      filter: false,
      valueGetter: (params: ValueGetterParams<MarketSummary.Metric>) => {
        return params.data?.price_offset_days != null ? 'price' : params.data?.definition
      },
    },
    {
      field: 'dimset',
      sortable: false,
      filter: false,
      headerName: 'Dimensions',
      cellRenderer: (params: CustomCellEditorProps<MarketSummary.Metric>) => {
        const entries = Object.entries({
          Annualisation: params.data.dimset?.annualisation,
          'Return Frequency': params.data.dimset?.return_frequency,
        })

        return (
          <div className={'flex items-center gap-2 h-full'}>
            {entries.map(([key, value]) => {
              return (
                key &&
                value && (
                  <Badge className={'gap-2'} variant={'outline'}>
                    <span>{key}</span>
                    <span className={'font-bold'}>{value}</span>
                  </Badge>
                )
              )
            })}
          </div>
        )
      },
    },
    {
      field: 'event_name',
      headerName: 'Event',
      sortable: false,
      filter: false,
    },
    {
      field: 'price_offset_days',
      headerName: 'Price Offset (days)',
      sortable: false,
      filter: false,
    },
  ])
}
